import { useEffect, useRef, useState } from 'react';
import DownIcon from 'src/icons/down';
import { LOGO_URL, NETWORK_NAME_LABELS } from 'src/utils/constants';
import Input from '../../components/input/Input';
import doubleArrows from 'src/icons/double-arrows.svg';
import Button from '../../components/button/Button';
import toast from 'react-hot-toast';
import API, { MerchantAPI } from 'src/utils/api';
import Loader from '../../components/loader/Loader';
import RoundPaginationButtons from '../../components/round-pagination-buttons/RoundPaginationButtons';
import { useNavigate } from 'react-router-dom';
import ErrorState from 'src/components/error-state/ErrorState';
import { removeTrailingZeros } from 'src/utils/strings';
import LockIcon from 'src/icons/lock';
import { useUserProfile } from 'src/context/user-context/UserProvider';

const BALANCES_PER_PAGE = 10;

const Withdraw = () => {
    const navigate = useNavigate();
    // User Profile
    const { email } = useUserProfile();
    // Fees
    const [fees, setFees] = useState();
    const [requireToPayFees, setRequireToPayFees] = useState();
    const [totalFees, setTotalFees] = useState();
    // Balances and prices
    const [balances, setBalances] = useState();
    const [prices, setPrices] = useState();
    const fetchingBalances = useRef(false);
    const fetchingFees = useRef(false);
    // Pages
    const [balancesTotalPages, setBalancesTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [balancesToShow, setBalancesToShow] = useState();
    // Withdraw states
    const [balanceToWithdraw, setBalanceToWithdraw] = useState();
    const [subtitle, setSubtitle] = useState('Select a coin and network to withdraw crypto from');
    const [tokenAmount, setTokenAmount] = useState();
    const [USDAmount, setUSDAmount] = useState();
    const [withdrawAddress, setWithdrawAddress] = useState();
    const [networkFee, setNetworkFee] = useState();
    const [maxWithdrawAmount, setMaxWithdrawAmount] = useState();
    // UI State
    const [fetchFeesLoading, setFetchFeesLoading] = useState(true);
    const [fetchBalancesLoading, setFetchBalancesLoading] = useState(true);
    const [fetchMaxWithdrawLoading, setFetchMaxWithdrawLoading] = useState(false);
    const [createPaymentLoading, setCreatePaymentLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (fetchingBalances.current) return; // make sure to fetch data only at first render
        const fetchBalances = async () => {
            fetchingBalances.current = true;
            try {
                const response = await API.get('/accounts/balances');

                const filteredBalances = response.data.balances.filter((balance) => balance.amount > 0);
                const pricesResponse = response.data.prices;

                setBalances(filteredBalances);
                setPrices(pricesResponse);
                setBalancesTotalPages(Math.ceil(filteredBalances.length / BALANCES_PER_PAGE));
                setBalancesToShow(filteredBalances.slice(0, BALANCES_PER_PAGE));
                setFetchBalancesLoading(false);

                fetchingBalances.current = false;
            } catch (error) {
                toast.error('Failed to fetch');
                setFetchBalancesLoading(false);
                setError(true);
                fetchingBalances.current = false;
            }
        };

        setFetchBalancesLoading(true);
        fetchBalances();
    }, []);

    useEffect(() => {
        if (fetchingFees.current) return; // make sure to fetch data only at first render
        const fetchFees = async () => {
            fetchingFees.current = true;
            try {
                const response = await API.get('/fees/all');

                setFees(response.data.fees);
                setTotalFees(response.data.total);
                setRequireToPayFees(response.data.needToPayFees);
                setFetchFeesLoading(false);

                fetchingFees.current = false;
            } catch (error) {
                toast.error('Failed to fetch');
                setFetchFeesLoading(false);
                setError(true);
                fetchingFees.current = false;
            }
        };

        setFetchFeesLoading(true);
        fetchFees();
    }, []);

    const onPayFeesClick = async () => {
        try {
            setCreatePaymentLoading(true);

            const response = await MerchantAPI.post(`/api/merchant/create-payment`, {
                formId: process.env.REACT_APP_FEES_FORM_ID,
                costInUSD: `${totalFees}`,
                successURL: `${window.location.href}`,
                cancelURL: `${window.location.href}`,
                callbackEndpoint: `${process.env.REACT_APP_API_BASE_URL}/fees/pay`,
                productNames: ['Payonix Transaction Fees'],
                logoURL: LOGO_URL,
                productsImagesURLs: [LOGO_URL],
                customerEmail: email,
                extraDataForCallback: JSON.stringify({ feesPaid: fees.map(({ id, fee }) => ({ id, amount: fee })) }),
            });

            window.location.href = response.data.paymentPageURL;
        } catch (error) {
            toast.error('Something unexpected happened, please try again later');
            setCreatePaymentLoading(false);
            setError(true);
        }
    };

    useEffect(() => {
        if (!balanceToWithdraw) return;

        const fetchMaxWithdrawAmount = async () => {
            setFetchMaxWithdrawLoading(true);
            setSubtitle('Enter the amount to withdraw and the address you want to withdraw to');

            try {
                const response = await API.post('/accounts/max-withdraw-amount', {
                    network: balanceToWithdraw.network,
                    token: balanceToWithdraw.token,
                });
                if (response.status === 200) {
                    setNetworkFee(response.data.networkFee);
                    setMaxWithdrawAmount(response.data.maxWithdrawAmount);
                }
                setFetchMaxWithdrawLoading(false);
            } catch (error) {
                setFetchMaxWithdrawLoading(false);
                setBalanceToWithdraw(null);
                setError(true);
                toast.error('An unexpected error occured, please try again later');
            }
        };

        fetchMaxWithdrawAmount();
    }, [balanceToWithdraw]);

    useEffect(() => {
        if (!balances) return;
        setBalancesToShow(balances.slice((currentPage - 1) * BALANCES_PER_PAGE, BALANCES_PER_PAGE * currentPage));
    }, [balances, currentPage]);

    const onTokenAmountChange = (newTokenAmount) => {
        if (!balanceToWithdraw) return;
        setTokenAmount(newTokenAmount);
        const expectedUSDAmount = newTokenAmount * prices[balanceToWithdraw.token];
        if (USDAmount !== expectedUSDAmount) {
            setUSDAmount(expectedUSDAmount.toFixed(2));
        }
    };

    const onUSDAmountChange = (newUSDAmount) => {
        if (!balanceToWithdraw) return;
        setUSDAmount(newUSDAmount);
        const expectedTokenAmount = newUSDAmount / prices[balanceToWithdraw.token];
        if (tokenAmount !== expectedTokenAmount) {
            setTokenAmount(expectedTokenAmount.toFixed(8));
        }
    };

    const onCancelClick = (toastID) => toast.dismiss(toastID);

    const onConfirmClick = async (toastID) => {
        toast.dismiss(toastID);
        try {
            const response = await API.post('/accounts/withdraw', {
                network: balanceToWithdraw.network,
                destination: withdrawAddress,
                token: balanceToWithdraw.token,
                amount: tokenAmount,
            });
            if (response.status === 200) {
                toast.success(
                    'Withdraw successful. The transaction is pending and will be completed in a few moments',
                    { duration: 5000 }
                );
                setTimeout(() => {
                    navigate('/');
                }, 3000);
            }
        } catch (error) {
            if (error.response.status === 400 && error.response.data.errors[0].msg === 'Invalid address') {
                return toast.error(`Invalid address for the ${NETWORK_NAME_LABELS[balanceToWithdraw.network]} network`);
            }
            setBalanceToWithdraw(null);
            toast.error('Failed to withdraw, an unexpected error occured');
            setTimeout(() => {
                navigate('/');
            }, 3000);
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        toast(
            (t) => (
                <p>
                    You are about to withdraw {tokenAmount} {balanceToWithdraw.token} (${USDAmount}) on the{' '}
                    {NETWORK_NAME_LABELS[balanceToWithdraw.network]}. Including the network fee,{' '}
                    <span className="underline">
                        you will spend {tokenAmountWithFees()} {balanceToWithdraw.token} in total
                    </span>
                    <br />
                    <br />
                    <b>
                        Please make sure that the address you entered is on the{' '}
                        {NETWORK_NAME_LABELS[balanceToWithdraw.network]}. Otherwise the transaction will fail and your
                        funds will be lost forever.
                    </b>
                    <br />
                    <br />
                    <div className="flex justify-between mt-4">
                        <Button text="Cancel" className="mr-1 w-1/2" onClick={() => onCancelClick(t.id)} />
                        <Button primary text="Confirm" className="ml-1 w-1/2" onClick={() => onConfirmClick(t.id)} />
                    </div>
                </p>
            ),
            { duration: 100000 }
        );
    };

    const onNextClick = () => {
        if (currentPage === balancesTotalPages) return;
        setCurrentPage(currentPage + 1);
    };

    const onPrevClick = () => {
        if (currentPage === 1) return;
        setCurrentPage(currentPage - 1);
    };

    const tokenAmountWithFees = () => {
        return (parseFloat(tokenAmount) + parseFloat(networkFee)).toFixed(8); // add network fee
    };

    if (fetchBalancesLoading || fetchFeesLoading || fetchMaxWithdrawLoading || createPaymentLoading) {
        return (
            <div className="px-10 h-screen w-screen flex items-center justify-center">
                <Loader />
            </div>
        );
    }

    if (error) {
        return <ErrorState />;
    }

    if (balanceToWithdraw && maxWithdrawAmount <= 0) {
        return (
            <div className="px-10 pt-24" title={`Withdraw ${balanceToWithdraw ? balanceToWithdraw.token : 'funds'}`}>
                <button className="flex items-center" onClick={() => setBalanceToWithdraw(null)}>
                    <DownIcon color={'#000000'} className="rotate-90 mr-2" /> Back
                </button>
                <p className="p-24 text-center">
                    Insufficient funds to withdraw. Try with another token or add funds first.
                </p>
            </div>
        );
    }

    return (
        <div className="relative px-4 md:px-10 pt-24">
            {balanceToWithdraw && (
                <button className="flex items-center" onClick={() => setBalanceToWithdraw(null)}>
                    <DownIcon color={'#000000'} className="rotate-90 mr-2" /> Back
                </button>
            )}
            <strong className="text-center w-full block text-2xl">{`Withdraw ${
                balanceToWithdraw ? balanceToWithdraw.token : 'funds'
            }`}</strong>

            {requireToPayFees && (
                <div className="flex flex-col justify-center items-center mt-8 mb-8">
                    <LockIcon className="h-10 w-10" />
                    <strong className="text-lg mt-2">You need to pay fees to be allowed to withdraw funds</strong>
                    <div className="w-96 rounded-xl bg-gray-light p-4 my-4">
                        <ul>
                            {fees.map((fee, i) => (
                                <li key={i} className="flex justify-between items-center mb-1">
                                    <span className="text-gray-darker">{fee.name}</span>
                                    <span className="font-bold">${fee.fee}</span>
                                </li>
                            ))}
                            <li className="flex justify-end items-center mt-4">
                                <span className="font-bold text-base">Total:&nbsp;&nbsp;${totalFees}</span>
                            </li>
                        </ul>
                    </div>
                    <Button text="Pay fees & Enable withdraw" primary className="w-96" onClick={onPayFeesClick} />
                </div>
            )}

            <span className="text-gray-darker text-center w-full block mb-4 mt-8">{subtitle}</span>
            {balanceToWithdraw ? (
                <form className="md:w-1/2 m-auto" onSubmit={onSubmit}>
                    <div className="flex justify-between  mt-6">
                        <div className="flex flex-col w-1/2">
                            <Input
                                label={`Amount (${balanceToWithdraw.token})`}
                                value={tokenAmount}
                                setValue={onTokenAmountChange}
                                className="mb-1"
                            />
                            <button
                                className="text-xs underline p-0 w-max ml-2"
                                onClick={() => onTokenAmountChange(maxWithdrawAmount)}
                                type="button"
                            >
                                Max: {maxWithdrawAmount} {balanceToWithdraw.token}
                            </button>
                        </div>
                        <img src={doubleArrows} alt="double arrows" className="mx-2 mt-2" />
                        <Input label="Amount (USD)" value={USDAmount} setValue={onUSDAmountChange} className="w-1/2" />
                    </div>
                    <Input
                        label={`Withdraw address (${NETWORK_NAME_LABELS[balanceToWithdraw.network]} network)`}
                        value={withdrawAddress}
                        setValue={setWithdrawAddress}
                        placeholder="Withdraw address"
                        className="mt-10"
                    />
                    <span className="mt-10 block">
                        Estimated network fee: {networkFee} {balanceToWithdraw.token} (≈ $
                        {(networkFee * prices[balanceToWithdraw.token]).toFixed(2)})
                    </span>
                    {tokenAmount && (
                        <span className="mt-2 block">
                            Including the network fee, you will spend:{' '}
                            <b className="text-lg">
                                {tokenAmountWithFees()} {balanceToWithdraw.token} (≈ $
                                {(tokenAmountWithFees() * prices[balanceToWithdraw.token]).toFixed(2)})
                            </b>
                        </span>
                    )}
                    <Button primary text="Withdraw" className="w-full mt-12" />
                </form>
            ) : (
                <div className="flex flex-col items-center">
                    {balancesToShow.map((balance, i) => (
                        <button
                            key={i}
                            className={`bg-gray-light rounded-xl flex px-6 py-4 mt-2 items-center ${
                                requireToPayFees ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            onClick={() => {
                                if (requireToPayFees) return;
                                setBalanceToWithdraw(balance);
                            }}
                            disabled={requireToPayFees}
                        >
                            <img
                                src={`/crypto-icons/${balance.token.toLowerCase()}.svg`}
                                alt={`${balance.token} icon`}
                                width={40}
                                className="mr-2"
                            />
                            <div className="flex flex-col mr-24">
                                <span className="text-lg text-left">{balance.token}</span>
                                <span className="text-xs text-gray-darker text-left">
                                    On the {NETWORK_NAME_LABELS[balance.network]} network
                                </span>
                            </div>
                            <div className="flex flex-col ml-auto">
                                <span className="text-md text-right">
                                    {removeTrailingZeros(balance.amount.toFixed(8))} {balance.token}
                                </span>
                                <span className="text-xs text-gray-darker text-right mt-2">
                                    ≈ ${(balance.amount * prices[balance.token]).toFixed(2)}
                                </span>
                            </div>
                            <DownIcon color={'#000000'} className="rotate-270 ml-6" />
                        </button>
                    ))}
                    {balancesTotalPages > 1 && (
                        <RoundPaginationButtons
                            pages={balancesTotalPages}
                            currentPage={currentPage}
                            onNextClick={onNextClick}
                            onPrevClick={onPrevClick}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default Withdraw;
